import type { SearchConversationEnriched, SearchQueue } from 'lib-search-types';
import { convertChannelCasing } from 'lib-shared-api-utils';
import type { Channel } from 'lib-shared-types-channels';

import type { WebSocketConversationStatusChangedInboundMessage } from 'middleware/webSocket';

/** The queue settings the WebSocket message leaves out, for a queue that isn't cached yet. */
const defaultQueueSettings: Omit<SearchQueue, 'id' | 'name' | 'is_restricted'> =
  {
    is_default: false,
    is_archived: false,
    available_offer_algorithms: null,
    calculation_method: null,
    offer_abandoned_conversations: false,
    member_list_type: null,
    offer_algorithm: null,
    offer_timeout: null,
    priority: null,
    wrapup_timeout: null,
    queue_thresholds: null,
    is_do_not_offer_enabled: false,
    do_not_offer_timeouts: {},
    personal_agent_offline_timeout: null,
    is_preferred_agent_enabled: false,
    preferred_agent_timeouts: null,
    preferred_agent_offline_timeout: null,
    is_personal_offer_prioritized: false,
    preferred_agent_schedule_id: null,
    personal_agent_schedule_id: null,
    preferred_agent_schedule_id_enabled: false,
    personal_agent_schedule_id_enabled: false,
  };

/**
 * Transforms the conversation from a `conversation:status_changed` WebSocket
 * message into a search result, for optimistic updates of the search results.
 *
 * The message has only part of what the search API returns:
 *
 * - **Updating a cached conversation:** pass it as `cachedConversation`. The
 *   fields the message has replace the cached ones, including the ones it
 *   clears, like an assignment or a follow-up. The fields it doesn't have, like
 *   the tags and custom fields, are kept. The queue keeps its cached settings,
 *   like its thresholds, while the conversation stays in it.
 * - **Adding a new conversation:** only the fields the message has are set, and
 *   its queue gets placeholder settings until the search results are refetched.
 */
export function transformWebSocketConversationToSearchConversation(
  wsParams: WebSocketConversationStatusChangedInboundMessage['params'],
  cachedConversation?: SearchConversationEnriched,
): SearchConversationEnriched {
  const { conversation, requester, agent_preference } = wsParams;

  const cachedQueue =
    cachedConversation?.queue?.id === conversation.queue?.id
      ? cachedConversation?.queue
      : undefined;

  const queue: SearchQueue | undefined = conversation.queue
    ? {
        ...(cachedQueue ?? defaultQueueSettings),
        id: conversation.queue.id,
        name: conversation.queue.name,
        is_restricted: conversation.queue.is_restricted,
      }
    : undefined;

  return {
    ...cachedConversation,
    id: conversation.id,
    status: conversation.status ?? 'open',
    initial_channel: convertChannelCasing(
      conversation.initial_channel,
      'snake',
    ) as Channel,
    initial_direction: conversation.direction ?? 'inbound',
    subject: conversation.attributes?.subject,
    inbound_message_count: conversation.attributes?.inbound_message_count,
    outbound_message_count: conversation.attributes?.outbound_message_count,
    non_automated_outbound_message_count:
      conversation.attributes?.non_automated_outbound_message_count,
    language:
      conversation.attributes && 'language' in conversation.attributes
        ? conversation.attributes.language
        : undefined,
    agent_preference: agent_preference ?? undefined,
    assigned_to: conversation.assignment?.agent ?? undefined,
    assigned_at: conversation.assignment?.assigned_at ?? undefined,
    requested_by: requester,
    queue,
    queued_at: conversation.queued_at ?? undefined,
    created_at:
      conversation.created_at ??
      cachedConversation?.created_at ??
      new Date().toISOString(),
    is_abandoned: conversation.is_abandoned ?? undefined,
    is_closed: conversation.is_closed,
    last_message_created_at: conversation.last_message_created_at ?? undefined,
    pending_expiration_at: conversation.pending_expiration_at ?? undefined,
  };
}
